import React from 'react'
import { CgSpinner } from 'react-icons/cg'

import { cn } from '@/lib/utils'

type AsProp<C extends React.ElementType> = {
  component?: C
}

type PropsToOmit<C extends React.ElementType, P> = keyof (AsProp<C> & P)

type PolymorphicComponentProp<C extends React.ElementType, Props = object> = React.PropsWithChildren<
  Props & AsProp<C>
> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>

type ButtonProps = {
  label: string
  loading?: boolean
  className?: string
}

type PolymorphicButtonProps<C extends React.ElementType> = PolymorphicComponentProp<C, ButtonProps>

const Button = <C extends React.ElementType = 'button'>({
  label,
  loading,
  className,
  component,
  ...rest
}: PolymorphicButtonProps<C>) => {
  const Component = component || 'button'

  const defaultClassName = `py-1.5 w-full rounded-md bg-[#F59405] hover:bg-[#F59405] text-white font-normal flex items-center justify-center transition-all ${
    loading ? 'cursor-not-allowed' : 'cursor-pointer'
  } disabled:bg-ui-primary/50`

  const combinedClassName = cn(defaultClassName, className)

  return (
    <Component className={combinedClassName} {...rest}>
      {loading ? (
        <>
          <CgSpinner
            className={`
              ${loading ? 'h-6 w-6 animate-spin' : 'h-0 w-0'}

              transition-all
            `}
          />
          &nbsp;<p>{label}</p>
        </>
      ) : (
        label
      )}
    </Component>
  )
}

export default Button
